import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Scale, Newspaper, FlaskConical, ExternalLink, Loader2, BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import JourneyStep from "@/components/home/JourneyStep";

const VIDEO_URL = "https://www.tiktok.com/@lelav89_/video/7638629829339860254";
const CREATOR_HANDLE = "@lelav89_";

type EvidenceItem = {
  type: "legislation" | "news" | "study";
  title: string;
  source: string;
  url?: string;
  summary: string;
  focusArea: string;
  date?: string;
};

type Insight = { video_url: string; summary: string; ideas: { title: string; blurb: string; focusArea: string }[] };
type EvidenceCache = { video_url: string; items: EvidenceItem[] };

const typeMeta: Record<EvidenceItem["type"], { label: string; icon: typeof Scale; tone: string }> = {
  legislation: { label: "Legislation", icon: Scale, tone: "bg-issue-justice/10 text-issue-justice border-issue-justice/20" },
  news: { label: "News", icon: Newspaper, tone: "bg-primary/10 text-primary border-primary/20" },
  study: { label: "Study", icon: FlaskConical, tone: "bg-issue-health/10 text-issue-health border-issue-health/20" },
};

const SupportingEvidenceSection = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["supporting-evidence", VIDEO_URL],
    queryFn: async (): Promise<EvidenceItem[]> => {
      // Need ideas first to seed the prompt
      const { data: insight } = await supabase
        .from("tiktok_video_insights")
        .select("*")
        .eq("video_url", VIDEO_URL)
        .maybeSingle();
      if (!insight) throw new Error("No insight available");
      const ins = insight as unknown as Insight;

      const { data: cached } = await supabase
        .from("tiktok_supporting_evidence")
        .select("*")
        .eq("video_url", VIDEO_URL)
        .maybeSingle();
      if (cached) return (cached as unknown as EvidenceCache).items;

      const { data: fresh, error: fnErr } = await supabase.functions.invoke("find-supporting-evidence", {
        body: {
          video_url: VIDEO_URL,
          creator_handle: CREATOR_HANDLE,
          summary: ins.summary,
          ideas: ins.ideas,
        },
      });
      if (fnErr) throw fnErr;
      return (fresh as EvidenceCache).items;
    },
    staleTime: 1000 * 60 * 60 * 12,
  });

  return (
    <JourneyStep
      step="04"
      label="Research"
      tone="muted"
      title={<>What the <span className="text-gradient-primary">data</span> says</>}
      subhead={`Our research team pairs every client thesis with the Alabama legislation, reporting, and studies that back it up. Example: evidence behind the themes ${CREATOR_HANDLE} raises.`}
    >
        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Curating evidence...
          </div>
        )}

        {error && (
          <p className="text-muted-foreground">Could not load supporting evidence right now.</p>
        )}

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.map((item, i) => {
              const meta = typeMeta[item.type] ?? typeMeta.news;
              const Icon = meta.icon;
              const Wrapper: React.ElementType = item.url ? "a" : "div";
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="min-w-0"
                >
                  <Wrapper
                    {...(item.url ? { href: item.url, target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="group block h-full min-w-0 rounded-[var(--radius)] border border-border bg-card/60 hover:bg-card hover:border-primary/40 transition-all p-5 shadow-card overflow-hidden"
                  >
                    <div className="flex items-start justify-between mb-3 gap-3">
                      <Badge variant="outline" className={`${meta.tone} text-xs font-medium`}>
                        <Icon className="h-3 w-3 mr-1" />
                        {meta.label}
                      </Badge>
                      {item.url && (
                        <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-0.5" />
                      )}
                    </div>
                    <h3 className="font-display text-base font-semibold text-foreground mb-2 leading-snug group-hover:text-primary transition-colors break-words">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-4 break-words">
                      {item.summary}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-border text-xs">
                      <span className="text-muted-foreground truncate pr-2">
                        {item.source}{item.date ? ` · ${item.date}` : ""}
                      </span>
                      <span className="text-primary/80 font-medium uppercase tracking-wider text-[10px] whitespace-nowrap">
                        {item.focusArea}
                      </span>
                    </div>
                  </Wrapper>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-8 flex items-center gap-2 text-xs text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5" />
          AI-curated from public Alabama sources. Always verify before citing.
        </div>
    </JourneyStep>
  );
};

export default SupportingEvidenceSection;